import { useState, useEffect, useCallback } from 'react';
import { authService, UserInfo } from './authService';

interface UseAuthReturn {
  user: UserInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

export const useAuth = (): UseAuthReturn => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize auth and check for existing session
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await authService.initialize();
        
        // Try to sign in silently if user was previously authenticated
        const userInfo = await authService.signInSilent();
        if (userInfo) {
          setUser(userInfo);
        }
      } catch (err) {
        console.error('Auth initialization failed:', err);
        setError('Failed to initialize authentication');
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const signIn = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const userInfo = await authService.signInPopup();
      setUser(userInfo);
    } catch (err: any) {
      const errorMessage = authService.handleAuthError(err);
      
      // Add specific guidance for the SPA configuration error
      let enhancedError = errorMessage;
      if (err.errorCode === "invalid_request" && err.errorMessage?.includes("Cross-origin token redemption")) {
        enhancedError = "Configuration Error: Your Azure AD app must be configured as a Single-Page Application (SPA). Please check AZURE_AD_FIX.md for detailed instructions.";
      }
      
      setError(enhancedError);
      console.error('Sign in failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      setIsLoading(true);
      await authService.signOut();
      setUser(null);
      setError(null);
    } catch (err) {
      console.error('Sign out failed:', err);
      setError('Failed to sign out');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getAccessToken = useCallback(async () => {
    try {
      return await authService.getAccessToken();
    } catch (err) {
      console.error('Failed to get access token:', err);
      return null;
    }
  }, []);

  return {
    user,
    isAuthenticated: user !== null,
    isLoading,
    error,
    signIn,
    signOut,
    getAccessToken,
  };
};